import { RowTableDistributionEnum } from "../enums/RowTableDistributionEnum";

type GenerateRowLabelOptions = {
  start?: number;
  count: number;
  reverse?: boolean;
  type: RowTableDistributionEnum;
};

export const selectRowTableDistribution = (
  value: RowTableDistributionEnum,
  rowsQuantity: number,
  startsAt: number = 1,
  reverse: boolean = false
) => {
  return generateRowLabels({
    start: startsAt,
    count: rowsQuantity,
    reverse,
    type: value,
  });
};

const generateRowLabels = ({
  start = 1,
  count,
  reverse = false,
  type,
}: GenerateRowLabelOptions): string[] => {
  let result: string[] = [];

  switch (type) {
    case RowTableDistributionEnum.UPPER_CASE:
      result = generateLetters(start, count, true);
      break;

    case RowTableDistributionEnum.NUMBERS:
      result = Array.from({ length: count }, (_, i) => String(start + i));
      break;

    case RowTableDistributionEnum.ROMAN:
      result = Array.from({ length: count }, (_, i) => toRoman(start + i));
      break;

    default:
      return [];
  }

  return reverse ? result.reverse() : result;
};

const generateLetters = (
  start: number,
  count: number,
  upperCase: boolean
): string[] => {
  const letters: string[] = [];

  for (let i = 0; i < count; i++) {
    const index = start - 1 + i;
    letters.push(numberToLetters(index, upperCase));
  }

  return letters;
};

const numberToLetters = (num: number, upperCase: boolean): string => {
  const base = 26;
  const safeNum = num < 0 ? 0 : num;
  const letterIndex = safeNum % base;
  const repeat = Math.floor(safeNum / base) + 1;

  const result = String.fromCharCode(letterIndex + 65).repeat(repeat);

  return upperCase ? result : result.toLowerCase();
};

const toRoman = (num: number): string => {
  const romanMap = [
    { value: 1000, symbol: "M" },
    { value: 900, symbol: "CM" },
    { value: 500, symbol: "D" },
    { value: 400, symbol: "CD" },
    { value: 100, symbol: "C" },
    { value: 90, symbol: "XC" },
    { value: 50, symbol: "L" },
    { value: 40, symbol: "XL" },
    { value: 10, symbol: "X" },
    { value: 9, symbol: "IX" },
    { value: 5, symbol: "V" },
    { value: 4, symbol: "IV" },
    { value: 1, symbol: "I" },
  ];

  let result = "";
  let remaining = num;

  for (const item of romanMap) {
    while (remaining >= item.value) {
      result += item.symbol;
      remaining -= item.value;
    }
  }

  return result;
};

export type RowTableStartMode = "number" | "letter" | "roman" | "none";

export const getRowTableStartMode = (
  type: RowTableDistributionEnum
): RowTableStartMode => {
  switch (type) {
    case RowTableDistributionEnum.UPPER_CASE:
      return "letter";
    case RowTableDistributionEnum.NUMBERS:
      return "number";
    case RowTableDistributionEnum.ROMAN:
      return "roman";
    default:
      return "none";
  }
};

const romanToNumber = (roman: string): number => {
  const map: Record<string, number> = {
    I: 1,
    V: 5,
    X: 10,
    L: 50,
    C: 100,
    D: 500,
    M: 1000,
  };

  const clean = (roman || "").toUpperCase().replace(/[^IVXLCDM]/g, "");
  if (!clean) return 1;

  let total = 0;
  for (let i = 0; i < clean.length; i++) {
    const current = map[clean[i]];
    const next = map[clean[i + 1]];
    if (next && current < next) {
      total -= current;
    } else {
      total += current;
    }
  }

  return total > 0 ? total : 1;
};

const lettersToNumber = (letters: string): number => {
  const clean = (letters || "").replace(/[^a-zA-Z]/g, "").toUpperCase();
  if (!clean) return 1;

  const letterIndex = clean.charCodeAt(0) - 65;
  const repeat = clean.length;

  return letterIndex + (repeat - 1) * 26 + 1;
};

export const rowTableStartsAtToLabel = (
  type: RowTableDistributionEnum,
  startsAt: number
): string => {
  const value = Math.max(1, Math.floor(startsAt || 1));

  switch (getRowTableStartMode(type)) {
    case "letter":
      return numberToLetters(value - 1, true);
    case "roman":
      return toRoman(value);
    case "number":
      return String(value);
    default:
      return "";
  }
};

export const rowTableLabelToStartsAt = (
  type: RowTableDistributionEnum,
  label: string
): number => {
  switch (getRowTableStartMode(type)) {
    case "letter":
      return lettersToNumber(label);
    case "roman":
      return romanToNumber(label);
    case "number": {
      const parsed = parseInt(String(label).replace(/[^0-9]/g, ""), 10);
      return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
    }
    default:
      return 1;
  }
};
